import { useEffect, useMemo, useState } from "react";

import { Page } from "../../../components/page-base";
import useUserStore from "../../../store/user";
import { profileQualityService } from "../services";
import type { EstadoDeAcesso, PerfilQualidade, QualidadePorEmail } from "../types";
import { ESTADOS, desdeQuando, somar, totalComProblema } from "./estados";

export const ProfileQuality = () => {
  const { user } = useUserStore();
  const token = user?.token ?? "";

  const [linhas, setLinhas] = useState<QualidadePorEmail[]>([]);
  const [perfis, setPerfis] = useState<PerfilQualidade[]>([]);
  const [emailAberto, setEmailAberto] = useState<string | null>(null);
  const [estadoAberto, setEstadoAberto] = useState<EstadoDeAcesso | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    if (!token) return;
    setCarregando(true);
    profileQualityService
      .getQualidadePorEmail(token)
      .then(setLinhas)
      .finally(() => setCarregando(false));
  }, [token]);

  const abrir = async (email: string, estado: EstadoDeAcesso) => {
    setEmailAberto(email);
    setEstadoAberto(estado);
    setPerfis(await profileQualityService.getPerfis(token, { email, estado }));
  };

  const pedirAcesso = async (publicId: string) => {
    if (await profileQualityService.marcarAcessoSolicitado(token, publicId)) {
      setPerfis((atual) => atual.filter((perfil) => perfil.publicId !== publicId));
      setLinhas(await profileQualityService.getQualidadePorEmail(token));
    }
  };

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const comProblemaPrimeiro = [...linhas].sort(
      (a, b) => totalComProblema(b) - totalComProblema(a),
    );
    return termo
      ? comProblemaPrimeiro.filter((linha) => linha.email?.toLowerCase().includes(termo))
      : comProblemaPrimeiro;
  }, [linhas, busca]);

  return (
    <Page.Root>
      <div style={{ padding: "24px", width: "100%" }}>
        <Page.HeaderAux />

        <h1 style={{ fontSize: "22px", margin: "8px 0 4px" }}>Qualidade dos perfis</h1>
        <p style={{ color: "#4a5568", margin: "0 0 20px" }}>
          Saúde do acesso a cada perfil, por e-mail que gerencia a conta no Google.
        </p>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
          {ESTADOS.map((estado) => (
            <div
              key={estado.chave}
              title={estado.explicacao}
              style={{
                border: "1px solid #e2e8f0",
                borderLeft: `4px solid ${estado.cor}`,
                borderRadius: "8px",
                padding: "12px 16px",
                minWidth: "150px",
                background: "#fff",
              }}
            >
              <div style={{ fontSize: "24px", fontWeight: 700, color: estado.cor }}>
                {somar(linhas, estado.contagem)}
              </div>
              <div style={{ fontSize: "13px", color: "#4a5568" }}>{estado.rotulo}</div>
            </div>
          ))}
        </div>

        <input
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          placeholder="Filtrar por e-mail"
          style={{
            width: "100%",
            maxWidth: "420px",
            padding: "10px 12px",
            border: "1px solid #cbd5e0",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        />

        {carregando ? (
          <p>Carregando...</p>
        ) : (
          <div style={{ overflowX: "auto", background: "#fff", borderRadius: "8px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "820px" }}>
              <thead>
                <tr style={{ background: "#f7fafc", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px" }}>E-mail gestor</th>
                  <th style={{ padding: "10px 12px" }}>Perfis</th>
                  {ESTADOS.map((estado) => (
                    <th key={estado.chave} style={{ padding: "10px 12px" }} title={estado.explicacao}>
                      {estado.rotulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiveis.map((linha) => (
                  <tr key={linha.email} style={{ borderTop: "1px solid #edf2f7" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{linha.email}</td>
                    <td style={{ padding: "10px 12px" }}>{linha.total}</td>
                    {ESTADOS.map((estado) => {
                      const quantos = estado.contagem(linha);
                      return (
                        <td key={estado.chave} style={{ padding: "10px 12px" }}>
                          {quantos > 0 ? (
                            <button
                              type="button"
                              onClick={() => abrir(linha.email, estado.chave)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: estado.cor,
                                fontWeight: 700,
                                fontSize: "15px",
                                padding: 0,
                              }}
                            >
                              {quantos}
                            </button>
                          ) : (
                            <span style={{ color: "#cbd5e0" }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {emailAberto && estadoAberto && (
          <div style={{ marginTop: "24px", background: "#fff", borderRadius: "8px", padding: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ fontSize: "16px", margin: 0 }}>
                {ESTADOS.find((estado) => estado.chave === estadoAberto)?.rotulo} — {emailAberto}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setEmailAberto(null);
                  setPerfis([]);
                }}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}
              >
                ×
              </button>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
              {perfis.map((perfil) => (
                <li
                  key={perfil.publicId}
                  style={{
                    borderTop: "1px solid #edf2f7",
                    padding: "10px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <span>
                    <strong>{perfil.name}</strong>
                    <span style={{ color: "#718096" }}>
                      {perfil.city ? ` — ${perfil.city}/${perfil.state ?? ""}` : ""}
                      {` · verificado ${desdeQuando(perfil.accessCheckedAt)}`}
                    </span>
                  </span>
                  {estadoAberto === "SEM_ACESSO" && (
                    <button
                      type="button"
                      onClick={() => pedirAcesso(perfil.publicId)}
                      style={{
                        border: "1px solid #b7791f",
                        color: "#b7791f",
                        background: "#fff",
                        borderRadius: "999px",
                        padding: "4px 12px",
                        cursor: "pointer",
                      }}
                    >
                      Marcar acesso solicitado
                    </button>
                  )}
                </li>
              ))}
              {perfis.length === 0 && <li style={{ padding: "10px 0" }}>Nenhum perfil nesta situação.</li>}
            </ul>
          </div>
        )}
      </div>
    </Page.Root>
  );
};
